/**
 * OC workflow transition helpers — status-based gating (no Django group RBAC).
 * Mirrors backend TRANSITION_MAP `from` sets for lab (non-client) actions.
 */

/** All known request status codes (keep aligned with OC_STATUS_LABELS). */
export const OC_ALL_STATUSES = [
  'SUBMITTED',
  'NEEDS_CLIENT_CLARIFICATION',
  'READY_FOR_DATA_REVIEW',
  'DATA_REVIEW_IN_PROGRESS',
  'CANDIDATES_IDENTIFIED',
  'NO_MATCHES_FOUND',
  'READY_FOR_TECHNICAL_REVIEW',
  'TECHNICAL_REVIEW_IN_PROGRESS',
  'TECHNICALLY_APPROVED',
  'PARTIALLY_APPROVED',
  'TECHNICAL_REVIEW_FAILED',
  'AWAITING_INTERNAL_APPROVAL',
  'AWAITING_CLIENT_CONFIRMATION',
  'CLIENT_REQUESTED_CHANGES',
  'CLIENT_APPROVED',
  'CLIENT_DECLINED',
  'PREPARATION_PENDING',
  'THAWING',
  'CULTURE_EXPANSION',
  'QC_IN_PROGRESS',
  'READY_FOR_SHIPMENT',
  'PARTIALLY_READY',
  'SHIPPED',
  'COMPLETED',
  'CANCELLED',
];

/**
 * Action → statuses from which the transition is allowed (lab actions only).
 * Keep in sync with backend `TRANSITION_MAP`.
 */
export const OC_TRANSITION_FROM = {
  request_client_clarification: [
    'SUBMITTED',
    'READY_FOR_DATA_REVIEW',
    'CLIENT_REQUESTED_CHANGES',
  ],
  send_to_data_review: [
    'SUBMITTED',
    'NEEDS_CLIENT_CLARIFICATION',
    'CLIENT_REQUESTED_CHANGES',
  ],
  start_data_review: ['READY_FOR_DATA_REVIEW'],
  identify_candidates: ['DATA_REVIEW_IN_PROGRESS', 'READY_FOR_DATA_REVIEW'],
  mark_no_matches: [
    'DATA_REVIEW_IN_PROGRESS',
    'READY_FOR_DATA_REVIEW',
    'CANDIDATES_IDENTIFIED',
  ],
  // Collapsed happy path (backend handoff): one hop to tech review from any data-review status.
  send_to_technical_review: [
    'READY_FOR_DATA_REVIEW',
    'DATA_REVIEW_IN_PROGRESS',
    'CANDIDATES_IDENTIFIED',
  ],
  start_technical_review: ['READY_FOR_TECHNICAL_REVIEW'],
  // First submit, plus revise a decision (for example Not recommended) before preparation starts.
  complete_technical_review: [
    'TECHNICAL_REVIEW_IN_PROGRESS',
    'READY_FOR_TECHNICAL_REVIEW',
    'AWAITING_CLIENT_CONFIRMATION',
    'CLIENT_APPROVED',
    'TECHNICAL_REVIEW_FAILED',
    'TECHNICALLY_APPROVED',
    'PARTIALLY_APPROVED',
  ],
  return_to_data_manager: [
    'TECHNICALLY_APPROVED',
    'PARTIALLY_APPROVED',
    'TECHNICAL_REVIEW_FAILED',
    'AWAITING_INTERNAL_APPROVAL',
  ],
  return_to_technician: [
    'TECHNICALLY_APPROVED',
    'PARTIALLY_APPROVED',
    'AWAITING_INTERNAL_APPROVAL',
  ],
  approve_client_response: [
    'TECHNICALLY_APPROVED',
    'PARTIALLY_APPROVED',
    'AWAITING_INTERNAL_APPROVAL',
  ],
  reject_request: [
    'SUBMITTED',
    'READY_FOR_DATA_REVIEW',
    'NO_MATCHES_FOUND',
    'TECHNICAL_REVIEW_FAILED',
    'AWAITING_INTERNAL_APPROVAL',
    'TECHNICALLY_APPROVED',
    'PARTIALLY_APPROVED',
  ],
  cancel_request: OC_ALL_STATUSES.filter(
    s => !['COMPLETED', 'CANCELLED', 'SHIPPED'].includes(s)
  ),
  start_preparation: ['CLIENT_APPROVED'],
  confirm_on_behalf_of_client: ['AWAITING_CLIENT_CONFIRMATION'],
  // Legacy bulk request-level prep marks — FE uses per-candidate preparation POST instead.
  mark_thawing: ['PREPARATION_PENDING', 'PARTIALLY_READY'],
  mark_culture_expansion: ['THAWING'],
  // mark_qc_in_progress removed from backend workflow; kept here so accidental UI calls still gate.
  mark_qc_in_progress: ['CULTURE_EXPANSION', 'THAWING'],
  mark_ready_for_shipment: [
    'QC_IN_PROGRESS',
    'PARTIALLY_READY',
    'PREPARATION_PENDING',
  ],
  mark_partially_ready: ['QC_IN_PROGRESS', 'THAWING', 'CULTURE_EXPANSION'],
  mark_as_shipped: ['READY_FOR_SHIPMENT', 'PARTIALLY_READY'],
  complete_request: ['SHIPPED'],
  revoke_client_token: [...OC_ALL_STATUSES],
};

/**
 * Whether a lab transition action is allowed from the current request status.
 * If status is missing, returns true so the API can decide (pages should pass status).
 *
 * @param {string} action
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function canOcTransition(action, status) {
  if (!action) return false;
  const allowed = OC_TRANSITION_FROM[action];
  if (!allowed) {
    // Unknown action — let the API validate.
    return true;
  }
  if (!status) return true;
  return allowed.includes(status);
}

/**
 * User-facing toast for a failed transition API response.
 *
 * @param {{ status?: number, messages?: string[] }|null|undefined} result
 * @returns {string}
 */
export function formatOcTransitionError(result) {
  const apiMessage = result?.messages?.[0];
  if (result?.status === 400) {
    return apiMessage || 'This step is not available in the current status';
  }
  if (result?.status === 403) {
    return (
      apiMessage ||
      'You are not authorized for organoid requests. Sign in as a lab user.'
    );
  }
  return apiMessage || 'Transition failed';
}
